import { useRef } from "react";
import { TradeForm, WidgetConfig } from "@cede/types";
import { forgeTokenURL } from "@cede/utils";
import { TradeFormAction } from "@/types";
import { getTimeLimitedPersistStorage } from "@/utils/general";
import { create } from "zustand";
import { persist, subscribeWithSelector } from "zustand/middleware";

type TradeFormState = {
  dispatchForm: (action: TradeFormAction<TradeForm>) => void;
  setInitialProps: (props: WidgetConfig) => void;
  resetForm: (keepSelectedAccount?: boolean) => void;
} & TradeForm;

const _initialFormState: TradeForm = {
  tokenFrom: null,
  amountFrom: "0",
  tokenTo: null,
  amountTo: "0",
  selectedAccount: null,
  market: null,
  orderSide: null,
  noCommonMarket: false,
  limitPrice: "0",
  initialProps: {
    tokenSymbols: undefined,
    auth: {
      identityToken: "",
      getAccessToken: () => Promise.resolve(""),
      identityProvider: "privy",
      refreshIdentityToken: null,
    },
  },
};

const PERSISTANCE_DURATION = 1000 * 60 * 10; // 10 minutes
export const useTradeFormStore = create(
  subscribeWithSelector(
    persist<TradeFormState>(
      (set) => ({
        ..._initialFormState,
        resetForm: (keepSelectedAccount = false) => {
          set({
            ..._initialFormState,
            selectedAccount: keepSelectedAccount ? useTradeFormStore.getState().selectedAccount : null,
            initialProps: useTradeFormStore.getState().initialProps,
          });
        },
        setInitialProps: (initialProps: WidgetConfig) =>
          set(() => {
            return {
              initialProps,
            };
          }),
        dispatchForm: (action) => {
          const state = useTradeFormStore.getState();

          if (action.field === "revertToken") {
            set({
              tokenFrom: state.tokenTo,
              tokenTo: state.tokenFrom,
            });
            return;
          }
          // If the user changes the tokenFrom or tokenTo, we need to reset the noCommonMarket flag
          // It will be set to true if the user selects two tokens that don't have a common market in further steps
          if (
            (action.field === "tokenFrom" && state.tokenFrom !== action.value) ||
            (action.field === "tokenTo" && state.tokenTo !== action.value)
          ) {
            set({ noCommonMarket: false });
          }

          // if the user changes the selected account, it should reset all the dependent fields
          if (action.field === "selectedAccount" && state.selectedAccount !== action.value) {
            set({
              market: null,
              tokenFrom: null,
              tokenTo: null,
              orderSide: null,
              noCommonMarket: false,
              amountFrom: "0",
              amountTo: "0",
            });
          }
          set({
            [action.field]: action.value,
          });
        },
      }),
      {
        name: "TradeStore",
        storage: getTimeLimitedPersistStorage<TradeFormState>({
          ttl: PERSISTANCE_DURATION,
          onHydration: (state) => {
            if (state.tokenFrom) {
              state.tokenFrom.img = forgeTokenURL(state.tokenFrom.value);
            }
            if (state.tokenTo) {
              state.tokenTo.img = forgeTokenURL(state.tokenTo.value);
            }
            return state;
          },
        }),
      },
    ),
  ),
);

export const useTradeForm = () => {
  const { dispatchForm, resetForm, ...tradeForm } = useTradeFormStore();
  const focusedInput = useRef<"amountFrom" | "amountTo" | null>(null);

  /**
   * Set the focused input. It helps to know which input is currently updated by the user and apply the correct computation
   */
  const setFocusedInput = (focused: "amountFrom" | "amountTo" | null) => {
    focusedInput.current = focused;
  };

  const hasSelectedBothTokens = !tradeForm.tokenFrom || !tradeForm.tokenTo;

  const addMax = (amount: string | number | null) => {
    setFocusedInput("amountFrom");
    dispatchForm({
      field: "amountFrom",
      value: amount?.toString() ?? "0",
    });
  };

  const resetAmounts = () => {
    dispatchForm({
      field: "amountFrom",
      value: "0",
    });
    dispatchForm({
      field: "amountTo",
      value: "0",
    });
  };

  return {
    tradeForm,
    dispatchForm,
    setFocusedInput,
    focusedInput,
    resetForm,
    hasSelectedBothTokens,
    addMax,
    resetAmounts,
  };
};
